// src/components/EditStationModal.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { kritischeAenderungBestaetigen } from "../utils/veranstaltungstag";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import * as Icons from "lucide-react";
import SymbolAuswahl from "./SymbolAuswahl";
import { iconVorschlag, normalisiereIcon } from "../utils/stationIcons";

// UI-Basics
import Modal from "./ui/Modal";
import Input from "./ui/Input";
import Textarea from "./ui/Textarea"; // ✅ NEU
import Select from "./ui/Select";
import Button from "./ui/Button";
import Spinner from "./ui/Spinner";
import Skeleton from "./ui/Skeleton";
import ZeitmessungFelder, {
  zeitmessungVon,
  zeitmessungPayload,
  sekundenText,
  versucheText,
} from "./ZeitmessungFelder";
import { SummenrechnerSchalter } from "./Summenrechner";
import QuizFelder, { quizVon, quizPayload } from "./QuizFelder";
import { teilweiseAusgesetzt } from "../utils/ausgesetzt";
import SponsorLogoFeld, { useSponsorLogo, logoSpeichern } from "./SponsorLogoFeld";

// "Erlaubt für" einer bestehenden Station als einer der drei Auswahlwerte.
// Stationen, die mit dem frueheren Fehler im Anlegen-Dialog gespeichert wurden,
// tragen allow_groups: true – die Auswahl zeigte dann keinen gueltigen Wert.
// Beim naechsten Speichern wird daraus "Alle".
const GRUPPEN = ["Alle", "Erwachsene", "Kinder"];
function erlaubteGruppe(station) {
  if (GRUPPEN.includes(station.allow_groups)) return station.allow_groups;
  return station.allow_kids === false ? "Erwachsene" : "Alle";
}

export default function EditStationModal({ open, onClose, station, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [editMode, setEditMode] = useState(false);

  const [stationName, setStationName] = useState("");
  const [stationNumber, setStationNumber] = useState("");
  const [supervisorName, setSupervisorName] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [bereich, setBereich] = useState("");
  const [gameDescription, setGameDescription] = useState("");
  const [rules, setRules] = useState("");
  const [scoreLogic, setScoreLogic] = useState("");
  const [materials, setMaterials] = useState("");
  const [funFact, setFunFact] = useState("");
  // Bestehende Stationen behalten ihr Symbol. Nur wenn noch keines gespeichert
  // ist (null), folgt es dem Namen, bis jemand eines antippt.
  const [iconGewaehlt, setIconGewaehlt] = useState(null);
  const [scoreDirection, setScoreDirection] = useState("higher");
  const [allowGroups, setAllowGroups] = useState("Alle");
  const [unit, setUnit] = useState("");
  const [stationType, setStationType] = useState("normal");
  const [correctValue, setCorrectValue] = useState("");
  const [zeitmessung, setZeitmessung] = useState("off");
  const [countdownSeconds, setCountdownSeconds] = useState("");
  const [stopwatchMaxSeconds, setStopwatchMaxSeconds] = useState("");
  const [stopwatchAttempts, setStopwatchAttempts] = useState("");
  const [quizWerte, setQuizWerte] = useState(quizVon(null));
  const [personenSumme, setPersonenSumme] = useState(false);
  // Aussetzen (z. B. Regen) je Kategorie: Kinder und Erwachsene werden getrennt
  // gewertet, ein Abbruch bei den Erwachsenen darf die Kinder nicht treffen.
  const [ausgesetztKinder, setAusgesetztKinder] = useState(false);
  const [ausgesetztErwachsene, setAusgesetztErwachsene] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const logo = useSponsorLogo(station?.station_number, station?.sponsor_logo === true, open && isAdmin);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (station) {
      setStationName(station.station_name || "");
      setStationNumber(station.station_number || "");
      setSupervisorName(station.supervisor_name || "");
      setSponsor(station.sponsor || "");
      setBereich(station.bereich || "");
      setGameDescription(station.game_description || "");
      setRules(station.rules || "");
      setScoreLogic(station.score_logic || "");
      setMaterials(station.materials || "");
      setFunFact(station.fun_fact || "");
      setIconGewaehlt(station.icon_name ? normalisiereIcon(station.icon_name) : null);
      setScoreDirection(station.score_direction || "higher");
      setUnit(station.unit || "");
      setStationType(station.station_type || "normal");
      setCorrectValue(station.correct_value ?? "");
      setZeitmessung(zeitmessungVon(station));
      setCountdownSeconds(sekundenText(station.countdown_seconds));
      setStopwatchMaxSeconds(sekundenText(station.stopwatch_max_seconds));
      setStopwatchAttempts(versucheText(station.stopwatch_attempts));
      setQuizWerte(quizVon(station));
      setPersonenSumme(station.person_sum === true);
      setAusgesetztKinder(station.ausgesetzt_kinder === true);
      setAusgesetztErwachsene(station.ausgesetzt_erwachsene === true);
      setAllowGroups(erlaubteGruppe(station));
      setError("");
      setEditMode(false);
    }
  }, [station, open]);

  const iconName =
    iconGewaehlt ?? iconVorschlag(stationName, { schaetzstation: stationType === "estimate" });

  const canSave = () =>
    isAdmin && editMode && stationName.trim() && !isSaving;

  const handleSave = async () => {
    if (!canSave()) return;

    // Nur wertungsrelevante Felder sind "kritisch" – einen Tippfehler in der
    // Spielbeschreibung am Veranstaltungstag zu korrigieren, ist harmlos.
    const wertungGeaendert =
      scoreDirection !== (station.score_direction || "higher") ||
      stationType !== (station.station_type || "normal") ||
      allowGroups !== erlaubteGruppe(station) ||
      ausgesetztKinder !== (station.ausgesetzt_kinder === true) ||
      ausgesetztErwachsene !== (station.ausgesetzt_erwachsene === true) ||
      (stationType === "quiz" &&
        (quizWerte.abzug !== (station.quiz_penalty === true) ||
          quizWerte.abzugKinder !== (station.quiz_penalty_kids === true))) ||
      (stationType === "estimate" && Number(correctValue) !== Number(station.correct_value));
    if (
      wertungGeaendert &&
      !(await kritischeAenderungBestaetigen(
        `Wertung der Station ${station.station_number} ändern (Richtung, Typ, Gruppen, ` +
          "Korrektwert oder Aussetzen) – die Punkte dieser Station werden neu berechnet."
      ))
    ) return;

    setIsSaving(true);
    setError("");

    const payload = {
      station_name: stationName.trim(),
      supervisor_name: supervisorName.trim(),
      sponsor: sponsor.trim(),
      bereich: bereich.trim(),
      game_description: gameDescription.trim(),
      rules: rules.trim(),
      score_logic: scoreLogic.trim(),
      materials: materials.trim(),
      fun_fact: funFact.trim(),
      icon_name: iconName,
      score_direction: scoreDirection,
      station_type: stationType,
      allow_groups: allowGroups,
      correct_value: stationType === "estimate" ? Number(correctValue) : null,
      // Zeitmessung: Countdown ODER Stoppuhr (+ Maximalzeit); bei
      // Schaetzstationen gibt es nichts zu messen.
      ...zeitmessungPayload({
        art: zeitmessung,
        countdownSeconds,
        maxSeconds: stopwatchMaxSeconds,
        versuche: stopwatchAttempts,
        // Quiz: die Uhr laeuft am Tablet, im Ergebnisdialog gibt es nichts zu messen.
        istSchaetzstation: stationType !== "normal",
      }),
      ...quizPayload(quizWerte, stationType === "quiz"),
      person_sum: stationType === "normal" && personenSumme,
      ausgesetzt_kinder: ausgesetztKinder,
      ausgesetzt_erwachsene: ausgesetztErwachsene,
      unit: unit.trim(),
    };

    try {
      // fetchWithAuth: bei abgelaufener Sitzung Neuanmeldung statt
      // "Netzwerkfehler"; Fehlertexte des Servers kommen als err.message.
      await fetchWithAuth(`${BACKEND_URL}/stations/${station.station_number}`, null, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Erst nach der Station: scheitert das Logo, sind die Texte schon sicher.
      try {
        await logoSpeichern(station.station_number, logo.aenderung);
      } catch (err) {
        onSaved();
        setError("Station gespeichert, aber das Logo nicht: " + (err.message || "Fehler"));
        return;
      }
      onSaved();
      setEditMode(false);
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || "Fehler beim Aktualisieren der Station");
    } finally {
      setIsSaving(false);
    }
  };

  // Station loeschen – das Backend lehnt ab, sobald Ergebnisse eingetragen
  // sind (deren Punkte wuerden sonst still verschwinden).
  const handleDelete = async () => {
    if (!isAdmin || isSaving) return;
    if (!(await kritischeAenderungBestaetigen(`Station ${station.station_number} löschen.`))) return;
    if (
      !window.confirm(
        `Station ${station.station_number} „${station.station_name || ""}“ endgültig löschen?\n\n` +
          "Das geht nur, solange dort noch keine Ergebnisse eingetragen sind."
      )
    ) {
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      await fetchWithAuth(`${BACKEND_URL}/stations/${station.station_number}`, null, {
        method: "DELETE",
      });
      onSaved();
      setEditMode(false);
      onClose();
    } catch (err) {
      setError(err.message || "Station konnte nicht gelöscht werden");
    } finally {
      setIsSaving(false);
    }
  };

  const IconPreview = Icons[iconName];

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="max-w-3xl" // ✅ Breiteres Modal
      title={
        <div className="flex items-center gap-2">
          {IconPreview && <IconPreview className="w-6 h-6" />}
          <span>Station {stationNumber}</span>
          {isAdmin && !editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="ml-2 text-secondary-400 hover:text-primary-500"
              title="Bearbeiten"
            >
              <Icons.Pencil className="w-5 h-5" />
            </button>
          )}
        </div>
      }
      footer={
        isAdmin && editMode ? (
          <>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isSaving}
              className="mr-auto"
            >
              Station löschen
            </Button>
            <Button
              variant="secondary"
              onClick={() => setEditMode(false)}
              disabled={isSaving}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!canSave()}
            >
              {isSaving ? (
                <>
                  <Spinner size={4} /> Speichern…
                </>
              ) : (
                "Speichern"
              )}
            </Button>
          </>
        ) : null
      }
    >
      {!station ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="grid gap-4">
          <Input
            label="Stationsname"
            value={stationName}
            onChange={(e) => setStationName(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Input
            label="Betreuer"
            value={supervisorName}
            onChange={(e) => setSupervisorName(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Input
            label="Sponsor"
            value={sponsor}
            onChange={(e) => setSponsor(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <SponsorLogoFeld logo={logo} disabled={isSaving} bearbeitbar={editMode} />

          {/* Flaeche auf dem Festgelaende. Gliedert die Spielliste auf dem
              Team-Zettel; gleiche Schreibweise = gemeinsame Ueberschrift.
              Bequemer setzt man das fuer alle Stationen auf einmal unter
              „Lageplan“. */}
          <Input
            label="Bereich (z. B. Halle, Parkplatz Nord)"
            value={bereich}
            onChange={(e) => setBereich(e.target.value)}
            maxLength={40}
            disabled={!editMode || isSaving}
          />

          {/* ✅ Mehrzeilige Textfelder */}
          <Textarea
            label="Spielbeschreibung"
            value={gameDescription}
            onChange={(e) => setGameDescription(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Textarea
            label="Regeln"
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Textarea
            label="Bewertung / Punkte-Logik"
            value={scoreLogic}
            onChange={(e) => setScoreLogic(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Input
            label="Materialien"
            value={materials}
            onChange={(e) => setMaterials(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Textarea
            label="Fun Fact"
            value={funFact}
            onChange={(e) => setFunFact(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <SymbolAuswahl
            value={iconName}
            onChange={setIconGewaehlt}
            disabled={!editMode || isSaving}
            hinweis={
              editMode && iconGewaehlt === null
                ? "Noch kein Symbol gespeichert – passend zum Namen vorgeschlagen."
                : ""
            }
          />

          <Input
            label="Einheit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={!editMode || isSaving}
          />

          <Select
            label="Bewertungsrichtung"
            value={scoreDirection}
            onChange={(e) => setScoreDirection(e.target.value)}
            options={[
              { value: "higher", label: "Höher ist besser" },
              { value: "lower", label: "Niedriger ist besser" },
            ]}
            disabled={!editMode || isSaving || stationType === "quiz"}
          />

          <Select
            label="Stations-Typ"
            value={stationType}
            onChange={(e) => {
              const value = e.target.value;
              setStationType(value);
              if (value === "estimate") setScoreDirection("lower");
              if (value === "quiz") setScoreDirection("higher");
            }}
            options={[
              { value: "normal", label: "Normale Station" },
              { value: "estimate", label: "Schätzstation" },
              { value: "quiz", label: "Quiz am Tablet (Wahr/Falsch, Mehrfachauswahl)" },
            ]}
            disabled={!editMode || isSaving}
          />

          {stationType === "estimate" && (
            <Input
              type={editMode ? "number" : "password"}
              label="Korrektwert"
              value={editMode ? correctValue : "****"}
              onChange={(e) => setCorrectValue(e.target.value)}
              disabled={!editMode || isSaving}
            />
          )}

          {/* Quiz: Spielzeiten und Regeln; Fragen und PIN unter „Quiz“. */}
          {stationType === "quiz" && (
            <QuizFelder
              werte={quizWerte}
              onChange={setQuizWerte}
              disabled={!editMode || isSaving}
              zeigeHinweise={editMode}
            />
          )}

          {/* Optionale Zeitmessung: Countdown oder Stoppuhr (siehe AddStationModal). */}
          {stationType === "normal" && (
            <ZeitmessungFelder
              art={zeitmessung}
              onArt={setZeitmessung}
              countdownSeconds={countdownSeconds}
              onCountdown={setCountdownSeconds}
              maxSeconds={stopwatchMaxSeconds}
              onMax={setStopwatchMaxSeconds}
              versuche={stopwatchAttempts}
              onVersuche={setStopwatchAttempts}
              disabled={!editMode || isSaving}
              zeigeHinweise={editMode}
            />
          )}

          {stationType === "normal" && (
            <SummenrechnerSchalter
              checked={personenSumme}
              onChange={setPersonenSumme}
              disabled={!editMode || isSaving}
              zeigeHinweis={editMode}
            />
          )}

          <Select
            label="Erlaubt für"
            value={allowGroups}
            onChange={(e) => setAllowGroups(e.target.value)}
            options={[
              { value: "Alle", label: "Alle (Kinder & Erwachsene)" },
              { value: "Erwachsene", label: "Nur Erwachsene" },
              { value: "Kinder", label: "Nur Kinder" },
            ]}
            disabled={!editMode || isSaving}
          />

          {/* Notfall-Funktion (z. B. Regen): bewusst unten und eingeklappt, damit sie
              im Normalfall nicht ins Auge faellt. Je Kategorie, weil Kinder und
              Erwachsene getrennt gewertet werden – sind die Kinder fertig, trifft
              ein Abbruch bei den Erwachsenen sie nicht. Nur sichtbar beim
              Bearbeiten oder wenn gerade etwas ausgesetzt ist (dann offen). */}
          {(editMode || ausgesetztKinder || ausgesetztErwachsene) && (
            <details
              className="rounded-lg border border-secondary-600 p-3"
              open={teilweiseAusgesetzt(station) || undefined}
            >
              <summary className="cursor-pointer text-sm text-secondary-400">
                {teilweiseAusgesetzt(station)
                  ? "⏸ Station ausgesetzt"
                  : "Station aussetzen (Notfall, z. B. Regen)"}
              </summary>
              <div className="mt-3 grid gap-2">
                {[
                  { gruppe: "Kinder", an: ausgesetztKinder, setAn: setAusgesetztKinder, zeigen: allowGroups !== "Erwachsene" },
                  { gruppe: "Erwachsene", an: ausgesetztErwachsene, setAn: setAusgesetztErwachsene, zeigen: allowGroups !== "Kinder" },
                ]
                  .filter((g) => g.zeigen)
                  .map((g) => (
                    <label
                      key={g.gruppe}
                      className={`flex items-center gap-3 rounded-md border p-2 ${
                        g.an ? "border-red-500 bg-red-500/10" : "border-transparent"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-5 w-5"
                        checked={g.an}
                        onChange={(e) => g.setAn(e.target.checked)}
                        disabled={!editMode || isSaving}
                      />
                      <span>
                        {g.an ? `⏸ Für ${g.gruppe} ausgesetzt` : `Für ${g.gruppe} aussetzen`}
                      </span>
                    </label>
                  ))}
                <p className="text-sm text-secondary-400">
                  Für die gewählte Gruppe werden keine Ergebnisse mehr angenommen, und die
                  Station zählt für sie nicht in der Wertung. Die andere Gruppe bleibt
                  unberührt. Bereits eingetragene Ergebnisse bleiben erhalten und zählen
                  wieder, sobald das Aussetzen aufgehoben ist.
                </p>
              </div>
            </details>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-800 text-red-200 rounded">
          {error}
        </div>
      )}
    </Modal>
  );
}
